import { BooleanConfigInput } from '@/engagements/base/admin/BooleanConfigInput';
import { DescriptionInput } from '@/engagements/base/admin/DescriptionInput';
import {
  Section,
  SectionContent,
  SectionHeading,
  SettingRowDescription,
  SettingRowHeading,
  SettingRowMultiInput,
  SettingRowInput,
  SettingsRow,
  SettingsTable,
} from '@/components/Section';
import { NumericConfigInput } from '@/engagements/base/admin/NumericConfigInput';
import { QRCodeCtaInput } from '@/engagements/base/admin/QRCodeCtaInput';
import { StringConfigInput } from '@/engagements/base/admin/StringConfigInput';
import { TitleInput } from '@/engagements/base/admin/TitleInput';
import { PhotoCarouselAdminConfig as AdminConfig } from '@/generated/graphql';
import { useAdminEngagementContext } from '@/providers/AdminEngagementProvider/AdminEngagementContext';
import { StringArrayConfigInput } from '@/engagements/base/admin/StringArrayConfigInput';
import {
  MAX_MAX_VISIBLE_PHOTOS,
  MIN_MAX_VISIBLE_PHOTOS,
  MIN_POLL_INTERVAL_SECONDS,
  pollIntervalMsToSeconds,
  resolveMaxVisiblePhotos,
  secondsToPollIntervalMs,
} from '@/engagements/photo-carousel/consts';

export function PhotoCarouselEditorView() {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    state: { editorMode, ...engagement },
  } = useAdminEngagementContext();

  return (
    <>
      <Section>
        <SectionHeading>Basic Info</SectionHeading>
        <SectionContent>
          <SettingsTable>
            <SettingsRow>
              <SettingRowHeading>Title</SettingRowHeading>
              <SettingRowDescription>
                Shows as a pulsing title above the QR code. Keep it short.
              </SettingRowDescription>
              <SettingRowInput>
                <TitleInput />
              </SettingRowInput>
            </SettingsRow>

            <SettingsRow>
              <SettingRowHeading>Instructions</SettingRowHeading>
              <SettingRowDescription>
                Shown as instructions to fans on mobile.
              </SettingRowDescription>
              <SettingRowInput>
                <DescriptionInput />
              </SettingRowInput>
            </SettingsRow>

            <SettingsRow>
              <SettingRowHeading>QR code label</SettingRowHeading>
              <SettingRowDescription>
                Displayed above the QR code. Keep it short.
              </SettingRowDescription>
              <SettingRowInput>
                <QRCodeCtaInput />
              </SettingRowInput>
            </SettingsRow>
          </SettingsTable>
        </SectionContent>
      </Section>
      <Section>
        <SectionHeading>Settings</SectionHeading>
        <SectionContent>
          <SettingsTable>
            <SettingsRow>
              <SettingRowHeading>"No photos" message</SettingRowHeading>
              <SettingRowDescription>
                Customize the message displayed when there are no photos.
              </SettingRowDescription>
              <SettingRowInput>
                <StringConfigInput<AdminConfig>
                  configKey="noPhotosMessage"
                  placeholder="Looking for photos..."
                />
              </SettingRowInput>
            </SettingsRow>

            <SettingsRow>
              <SettingRowHeading>Photos on screen</SettingRowHeading>
              <SettingRowDescription>
                Photos kept on screen. Old photos re-layer once there are more
                than this many.
              </SettingRowDescription>
              <SettingRowInput>
                <NumericConfigInput<AdminConfig>
                  id="max-visible-photos"
                  name="max-visible-photos"
                  configKey="maxVisiblePhotos"
                  min={MIN_MAX_VISIBLE_PHOTOS}
                  max={MAX_MAX_VISIBLE_PHOTOS}
                  fromValue={resolveMaxVisiblePhotos}
                />
              </SettingRowInput>
            </SettingsRow>

            <SettingsRow>
              <SettingRowHeading>Max submissions per user</SettingRowHeading>
              <SettingRowDescription>
                How many photos each person can send. 0 or empty means
                unlimited. Other people are not affected when someone hits the
                cap.
              </SettingRowDescription>
              <SettingRowInput>
                <NumericConfigInput<AdminConfig>
                  id="max-submissions-per-user"
                  name="max-submissions-per-user"
                  configKey="maxSubmissionsPerUser"
                  min={0}
                />
              </SettingRowInput>
            </SettingsRow>

            <SettingsRow>
              <SettingRowHeading>Photo rotation interval</SettingRowHeading>
              <SettingRowDescription>
                Seconds between Stage photo changes.
              </SettingRowDescription>
              <SettingRowInput>
                <NumericConfigInput<AdminConfig>
                  id="poll-interval-seconds"
                  name="poll-interval-seconds"
                  configKey="pollInterval"
                  min={MIN_POLL_INTERVAL_SECONDS}
                  fromValue={pollIntervalMsToSeconds}
                  toValue={secondsToPollIntervalMs}
                />
              </SettingRowInput>
            </SettingsRow>

            <SettingsRow>
              <SettingRowHeading>Ask for share permission</SettingRowHeading>
              <SettingRowDescription>
                If enabled, fans will be asked to share their photo on social
                media. You can customize the share prompt.
              </SettingRowDescription>
              <SettingRowInput>
                <BooleanConfigInput<AdminConfig> configKey="askSharePermission" />
              </SettingRowInput>
            </SettingsRow>

            {(engagement.config as AdminConfig)?.askSharePermission && (
              <SettingsRow>
                <SettingRowHeading>Share prompt</SettingRowHeading>
                <SettingRowDescription>
                  Customize the share prompt.
                </SettingRowDescription>
                <SettingRowInput>
                  <StringConfigInput<AdminConfig> configKey="sharePrompt" />
                </SettingRowInput>
              </SettingsRow>
            )}

            <SettingsRow>
              <SettingRowHeading>Require photo approval</SettingRowHeading>
              <SettingRowDescription>
                If enabled, all photos will need to be manually approved by the
                event organizer.
              </SettingRowDescription>
              <SettingRowInput>
                <BooleanConfigInput<AdminConfig> configKey="requireApproval" />
              </SettingRowInput>
            </SettingsRow>

            <SettingsRow forceColumn>
              <div className="flex w-full">
                <SettingRowHeading>Predefined captions</SettingRowHeading>
                <SettingRowDescription>
                  The captions that will be used for the photos.
                </SettingRowDescription>
              </div>
              <SettingRowMultiInput>
                <StringArrayConfigInput<AdminConfig> configKey="predefinedCaptions" />
              </SettingRowMultiInput>
            </SettingsRow>
          </SettingsTable>
        </SectionContent>
        {/* <pre>{JSON.stringify(engagement.config, null, 2)}</pre> */}
      </Section>
    </>
  );
}
